import React, { useState } from 'react';
import { X, IndianRupee, Loader } from 'lucide-react';
import { validatePromoCode } from '../lib/supabase';

interface DeliveryAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  collectible: {
    id: string;
    name: string;
    price: number;
  };
}

const SHIPPING_CHARGE = 50; // Default shipping charge in INR

export default function DeliveryAddressModal({ isOpen, onClose, onSubmit, collectible }: DeliveryAddressModalProps) {
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: ''
  });

  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState<string | null>(null);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(0);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;

    setIsApplyingPromo(true);
    setPromoError(null);

    try {
      const result = await validatePromoCode(promoCode.trim());
      
      if (result.valid) {
        setAppliedDiscount(result.discount_percent);
      } else {
        setPromoError(result.message);
      }
    } catch (err) {
      setPromoError('Failed to validate promo code');
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const removePromoCode = () => {
    setPromoCode('');
    setAppliedDiscount(0);
    setPromoError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      promo_code: appliedDiscount > 0 ? promoCode : null,
      discount_percent: appliedDiscount,
      shipping_charge: SHIPPING_CHARGE
    });
  };

  // Calculate prices
  const itemPrice = collectible.price;
  const discountAmount = Math.round(itemPrice * (appliedDiscount / 100));
  const discountedPrice = itemPrice - discountAmount;
  const totalPrice = discountedPrice + SHIPPING_CHARGE;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center overflow-y-auto p-4 z-50">
      <div className="relative bg-white rounded-xl w-full max-w-lg my-8">
        <div className="sticky top-0 bg-white border-b border-gray-200 rounded-t-xl z-10">
          <div className="p-6 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Delivery Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Order Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">{collectible.name}</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Item Price:</span>
                <span className="font-medium text-gray-900">₹{itemPrice.toLocaleString()}</span>
              </div>
              {appliedDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount ({appliedDiscount}%):</span>
                  <span>-₹{discountAmount.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <span>Shipping Charge:</span>
                <span className="font-medium text-gray-900">₹{SHIPPING_CHARGE}</span>
              </div>
              <div className="flex justify-between text-primary-600 font-medium pt-2 border-t">
                <span>Total Price:</span>
                <span>₹{totalPrice.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Promo Code Section */}
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Enter promo code"
                className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
              />
              <button
                type="button"
                onClick={applyPromoCode}
                disabled={!promoCode || isApplyingPromo}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-primary-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isApplyingPromo ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Applying...
                  </>
                ) : (
                  'Apply'
                )}
              </button>
            </div>

            {promoError && (
              <p className="text-sm text-red-600">{promoError}</p>
            )}

            {appliedDiscount > 0 && (
              <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-green-700 font-medium">
                    {appliedDiscount}% discount applied
                  </span>
                  <button
                    type="button"
                    onClick={removePromoCode}
                    className="text-green-600 hover:text-green-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-sm text-green-600 mt-1">
                  Original price: ₹{itemPrice.toLocaleString()}<br />
                  Discounted price: ₹{discountedPrice.toLocaleString()}<br />
                  Shipping charge: ₹{SHIPPING_CHARGE}<br />
                  Total price: ₹{totalPrice.toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Delivery Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                pattern="[0-9]{10}"
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                placeholder="10-digit mobile number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 1
              </label>
              <input
                type="text"
                name="address_line1"
                value={formData.address_line1}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                placeholder="House/Flat No., Building Name, Street"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 2 (Optional)
              </label>
              <input
                type="text"
                name="address_line2"
                value={formData.address_line2}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                placeholder="Area, Landmark"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                >
                  <option value="">Select State</option>
                  <option value="Andhra Pradesh">Andhra Pradesh</option>
                  <option value="Karnataka">Karnataka</option>
                  <option value="Kerala">Kerala</option>
                  <option value="Tamil Nadu">Tamil Nadu</option>
                  <option value="Telangana">Telangana</option>
                  {/* Add other states */}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PIN Code
              </label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                required
                pattern="[0-9]{6}"
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                placeholder="6-digit PIN code"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 pt-4 z-10">
            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
              >
                Continue to Payment
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}